import { FieldMergeFunction, gql, InMemoryCache, Reference } from '@apollo/client';
import { isEmpty, map } from 'lodash';

import ability from '@acl/ability';
import { GET_ALL_USER_PAYMENT_PROFILES, USER_EVENTS_CORE_FRAGMENT } from '@api';
import { TwoFactorMethods, UpdateEventSeriesMutationVariables, UserSessionData } from '@api/gql/graphql';
import { UserEventsCoreFragment } from '@api/operations/fragments/types/UserEventsCoreFragment';
import { USER_CART } from '@api/operations/queries/Cart.gql';
import { USER_FILES } from '@api/operations/queries/Files.gql';
import { GET_PRACTITIONER_SIGNATURE } from '@api/operations/queries/Practitioners.gql';
import { PaymentProfiles } from '@api/operations/queries/types/PaymentProfiles';
import { removeFromCache, resetCache } from '@src/utils/apollo';
import { deepMerge, removeDuplicateObjects, setStorageItem } from '@src/utils/helperFunctions';
import { AppActions } from '@src/utils/types';
// eslint-disable-next-line import/no-cycle
import { handleLogin, mergeSessionContext } from '@store/actions/authentication';
import {
  isLoggedInVar,
  layoutStateVar,
  navbarStateVar,
  userAlertsVar,
  userCartVar,
  userEventsVar,
  userMessagesVar,
  userSessionVar,
  userTasksVar,
  userWishlistVar
} from '@store/reactiveVars';
import { UserMessageType } from '@store/types';

/**
 * Pagination merge factory for `{ rows, ...meta }` shapes returned by the gateway.
 *
 * - Defensive against a missing `rows` field on either side (returns the side that has it,
 *   or an empty array). Previously a failed/empty response would crash with
 *   "Cannot read properties of undefined (reading 'rows')".
 * - Dedupes by `__ref` (cache reference) when rows are normalized — that's an O(n)
 *   primary-key compare. Falls back to JSON.stringify only for non-normalized rows.
 *   The previous `JSON.stringify` of every row on every merge was O(n*size) and
 *   produced different keys for objects whose properties were inserted in different
 *   orders, defeating the dedup it was supposed to provide.
 *
 * Note: This is the right shape for `keyArgs`-partitioned offset pagination only.
 * For cursor-based or page-number pagination, the merge would also need to inspect
 * `args` to slice rows into the correct offset.
 */
const mergeRowsField: FieldMergeFunction<any, any> = (existing, incoming) => {
  if (!incoming) return existing;
  if (!existing) return incoming;
  const existingRows = Array.isArray(existing.rows) ? existing.rows : [];
  const incomingRows = Array.isArray(incoming.rows) ? incoming.rows : [];
  return {
    ...incoming,
    rows: removeDuplicateObjects([...existingRows, ...incomingRows])
  };
};

/**
 * Merge for `getPaginatedFeedItems` (uses `elements`, not `rows`). Accumulates
 * pages for infinite scroll, then RE-SORTS the result by `publishDate`.
 *
 * The plain append+dedupe used for `rows` is wrong here: this feed POLLS and the
 * reload button refetches page 0, so newer items arrive in an already-merged
 * list and — kept in append order — land at the BOTTOM, making the timeline read
 * out of chronological order. Re-sorting by publishDate (honoring the requested
 * direction) keeps newest-first regardless of which page an item arrived on.
 */
const mergeElementsField: FieldMergeFunction<any, any> = (
  existing,
  incoming,
  { args, readField }
) => {
  if (!incoming) return existing;
  if (!existing) return incoming;
  const existingEls = Array.isArray(existing.elements) ? existing.elements : [];
  const incomingEls = Array.isArray(incoming.elements) ? incoming.elements : [];
  const merged = removeDuplicateObjects([...existingEls, ...incomingEls]);
  const direction = args?.sort?.order?.direction ?? 'DESC';
  const toTime = (ref: Reference): number => {
    const value = readField<string>('publishDate', ref);
    const time = value ? new Date(value).getTime() : 0;
    return Number.isNaN(time) ? 0 : time;
  };
  const sorted = [...merged].sort((a, b) => (
    direction === 'ASC' ? toTime(a) - toTime(b) : toTime(b) - toTime(a)
  ));
  return {
    ...incoming,
    elements: sorted
  };
};

export const GET_EVENTS = gql`
  ${USER_EVENTS_CORE_FRAGMENT}
  query GetEvents {
    events @client {
      ...UserEventsCoreFragment
    }
  }
`;

const reactiveFields = {
  UserSessionData() {
    return userSessionVar();
  },
  IsLoggedIn() {
    return isLoggedInVar();
  },
  UserCartData() {
    return userCartVar();
  },
  UserEventData() {
    return userEventsVar();
  },
  UserAlerts() {
    return userAlertsVar();
  },
  UserWishListData() {
    return userWishlistVar();
  },
  LayoutStore() {
    return layoutStateVar();
  },
  Messages() {
    return userMessagesVar();
  },
  NavbarStore() {
    return navbarStateVar();
  },
  Tasks() {
    return userTasksVar();
  }
};
/* eslint-disable @typescript-eslint/default-param-last,no-restricted-syntax,@typescript-eslint/no-shadow */
export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        ...reactiveFields,
        cart(existing) {
          return existing;
        },
        events: {
          read() {
            return userEventsVar();
          }
        },
        userAlerts: () => reactiveFields.UserAlerts(),
        locationPatientsFeed: {
          keyArgs: ['searchBy'],
          merge: mergeRowsField
        },
        getPaginatedFeedItems: {
          keyArgs: ['parameters', 'sort'],
          merge: mergeElementsField
        },
        practitionersFeed: {
          keyArgs: ['searchBy'],
          merge: mergeRowsField
        },
        appointmentList: {
          keyArgs: ['searchBy'],
          merge: mergeRowsField
        },
        getPaginatedOrderNotes: {
          keyArgs: ['searchBy'],
          merge: mergeRowsField
        },
        paginatedPrescriptionList: {
          keyArgs: ['searchBy', 'sort', 'pagination', ['size']],
          merge: mergeRowsField
        },
        userProductBundlesFeed: {
          keyArgs: ['searchBy', 'scope', 'pagination', ['size']],
          merge: mergeRowsField
        },
        catalogProducts: {
          keyArgs: ['searchBy', 'distance', 'options', 'sort', 'pagination']
        },
        getPaginatedTasks: {
          keyArgs: [
            'searchBy',
            ['queryKeyword', 'queryOperator', 'querySet'],
            'options',
            ['paranoid']
          ],
          merge: mergeRowsField
        },
        tasks: {
          read() {
            return userTasksVar();
          }
        },
        UserSession: {
          merge(_existing, incoming) {
            // Preserve the last-known staff context (org/location/staffRelation)
            // when a token silent-renew returns a half-null session, instead of
            // clobbering the working session. See mergeSessionContext.
            const incomingSession = incoming?.session ?? incoming;
            userSessionVar(mergeSessionContext(userSessionVar(), incomingSession));
            return incoming;
          }
        },
        getAllTasks: {
          merge(existing = [], incoming = []) {
            // Always return a value — returning undefined here erases the cache
            // entry, causing the next read to refetch and the reactive-var sync
            // to receive `[]` on the rebound.
            const merged = [...existing, ...incoming];
            userTasksVar(merged);
            return merged;
          }
        },
        getAllPatientConditions: {
          keyArgs: ['searchBy']
        },
        exploreDirectoryFiles: (existing) => existing && existing,
        getAllLocationPatients: {
          keyArgs: ['searchBy']
        },
        getAllUserEvents: {
          merge(existing = [], incoming = [], { cache: cacheCtx }) {
            // Read the (possibly empty) live `events` reactive var and combine with
            // the new server payload, then dedupe by event_id. The previous version
            // ran two passes that double-counted the new rows.
            const liveEvents = (cacheCtx.read({
              query: GET_EVENTS,
              optimistic: true
            }) as { events: UserEventsCoreFragment[] } | null)?.events ?? [];

            const merged = removeDuplicateObjects(
              [...liveEvents, ...incoming],
              // eslint-disable-next-line no-underscore-dangle
              (e: any) => e?.event_id ?? e?.__ref ?? JSON.stringify(e)
            );

            setStorageItem('userEvents', merged);
            userEventsVar(merged);
            return [...existing, ...incoming];
          }
        },
        getAllPaymentProfiles: {
          merge(existing = [], incoming) {
            const sessionData = reactiveFields.UserSessionData();
            userSessionVar({ ...sessionData, paymentProfiles: incoming });
            return [...existing, ...incoming];
          }
        },
        getAllUserMessages: {
          merge(_existing = [], incoming = []) {
            const prevMessages = reactiveFields.Messages() ?? [];
            const merged = removeDuplicateObjects(
              [...prevMessages, ...incoming],
              // eslint-disable-next-line no-underscore-dangle
              (m: any) => m?.message_id ?? m?.__ref ?? JSON.stringify(m)
            );
            userMessagesVar(merged);
            return merged;
          }
        },
        getAllClinicalNotes: {
          keyArgs: ['searchBy', 'options']
        },
        getAllServiceRequests: {
          keyArgs: ['searchBy', ['querySet']]
        }
      }
    },
    Mutation: {
      fields: {
        login: {
          merge(_existing = [], incoming, { cache }) {
            const paymentProfiles: PaymentProfiles = cache.read({
              query: GET_ALL_USER_PAYMENT_PROFILES,
              optimistic: true
            });

            if (paymentProfiles) {
              userSessionVar({
                ...incoming.session,
                paymentProfiles: paymentProfiles.getAllPaymentProfiles
              });
            }

            if (incoming.session && !incoming.session.twoFactorEnabled) {
              handleLogin(incoming, ability);
            }

            return incoming;
          }
        },
        // logout: {
        //   async merge(_existing, _incoming, { cache }) {
        //     handleLogout();
        //     await cache.reset({ discardWatches: true }).catch((err) => console.warn(err));
        //     return cache.gc({ resetResultCache: true, resetResultIdentities: true });
        //   }
        // },
        createTreatmentPlan: {
          merge(_, __, { cache }) {
            cache.evict({ fieldName: 'getPaginatedCarePlans' });
            cache.gc();
          }
        },
        deactivateEventSeries: {
          merge(_, __, { cache }) {
            cache.evict({ fieldName: 'getPaginatedOrders' });
            cache.evict({ fieldName: 'getPaginatedRecurringOrders' });
            cache.gc();
          }
        },
        addBulkPatientMedications: {
          merge(_, __, { cache }) {
            // The previous `cache.evict({})` was a no-op (eviction requires either
            // `id` or `fieldName`). Only evict the prescription list — the bulk-add
            // mutation can't possibly stale anything else.
            cache.evict({ fieldName: 'getPaginatedLocationPrescriptions' });
            cache.evict({ fieldName: 'paginatedPrescriptionList' });
            cache.gc();
          }
        },
        // An edit can move a usage between the chart's Active and Past lists, so drop the pages.
        updatePatientMedication: {
          merge(_, incoming, { cache }) {
            cache.evict({ fieldName: 'paginatedPrescriptionList' });
            cache.gc();
            return incoming;
          }
        },
        deleteMedicationUsage: {
          merge(_existing, _incoming, { variables, cache }) {
            const normalizedId = cache.identify({
              usage_id: variables.usageId,
              __typename: 'MedicationUsages'
            });
            cache.evict({ id: normalizedId });
            cache.evict({ fieldName: 'paginatedPrescriptionList' });
            cache.gc();
          }
        },
        userRegister: {
          merge(_existing = [], incoming) {
            handleLogin(incoming, ability);
            return incoming;
          }
        },
        createPaymentProfile: {
          merge: (existing = [], incoming) => {
            const userData = reactiveFields.UserSessionData();
            userSessionVar({
              ...userData,
              paymentProfiles: [...userData.paymentProfiles, incoming]
            });
            cache.modify({
              fields: {
                getAllPaymentProfiles() {
                  return [...userData.paymentProfiles, incoming];
                }
              }
            });
            return [...existing, incoming];
          }
        },
        updatePaymentProfile: {
          merge(existing = [], incoming, { variables }) {
            const userData = reactiveFields.UserSessionData();
            const previousProfiles = userData?.paymentProfiles ?? [];

            // Map-and-replace by id rather than splice(indexOf(...)) — when the id
            // wasn't present the previous code computed splice(-1, 1, incoming),
            // which deletes the LAST profile and inserts the new one in its place.
            const targetId = variables.paymentProfileId;
            const updatedProfiles = previousProfiles.map((profile) => (
              profile.authnet_payment_profile_id === targetId ? incoming : profile
            ));

            userSessionVar({ ...userData, paymentProfiles: updatedProfiles });
            return [...existing, incoming];
          }
        },
        updateUserNotification: {
          merge(_existing = [], incoming, { variables, cache, toReference }) {
            // // Build new arrays via map() instead of mutating in place. The previous
            // // version called splice(indexOf(undefined), ...) when an id wasn't found
            // // (silently overwriting the last item) and used Object.assign to mutate
            // // nested objects (changes weren't observed because references didn't change).
            const idsToUpdate = new Set(variables.notificationIds as string[]);
            const patch = variables.updatePayload as Record<string, unknown>;

            const fieldsToUpdate = {};
            Object.entries(patch).forEach(([key, value]) => {
              Object.assign(fieldsToUpdate, {
                [key]() {
                  return value;
                }
              });
            });
            idsToUpdate.forEach((id) => {
              const reference = toReference(({
                __typename: 'Notifications',
                notification_id: id
              }));
              cache.modify({
                id: cache.identify(reference),
                fields: fieldsToUpdate,
                broadcast: true
              });
            });
            return incoming;
          }
        },
        updateUserMessages: {
          merge(_existing, incoming) {
            // Index incoming messages by id, then build the new array with each
            // existing message either replaced or kept. Avoids the indexOf(find())
            // anti-pattern that would silently overwrite the last message when the
            // id wasn't found.
            const incomingById = new Map<string, UserMessageType>();
            for (const m of (incoming as UserMessageType[])) incomingById.set(m.message_id, m);

            const next = (reactiveFields.Messages() ?? []).map((m: UserMessageType) => (
              incomingById.has(m.message_id) ? incomingById.get(m.message_id)! : m
            ));

            return userMessagesVar(next);
          }
        },
        deletePaymentProfile: {
          merge: (existing = [], incoming, { variables, cache }) => {
            const userData = reactiveFields.UserSessionData();
            const updateProfiles = userData.paymentProfiles.filter(
              (profile) => profile.authnet_payment_profile_id !== variables.paymentProfileId
            );
            userSessionVar({
              ...userData,
              paymentProfiles: updateProfiles
            });
            cache.modify({
              fields: {
                getAllPaymentProfiles() {
                  return updateProfiles;
                }
              }
            });
            return [...existing, incoming];
          }
        },

        uploadFiles: {
          merge: (existing, incoming, { cache, variables, mergeObjects }) => {
            const uploadedFiles = variables.files.map((file) => file);
            // eslint-disable-next-line
            const previousPaths = <any>cache.readQuery({
              query: USER_FILES,
              variables: { recursive: true }
            });
            const uploadFolder = variables.folder ?? 'Files';

            const relatedPaths = previousPaths.exploreDirectoryFiles.paths.filter(
              (file) => file.folder.toLowerCase() === uploadFolder.toLowerCase()
            );

            const dataToWrite = {
              __typename: 'Bucket',
              folder: variables.folder ?? 'Files',
              files: [...relatedPaths.map((path) => path.files)].flat(Infinity)
            };

            for (const file of uploadedFiles as File[]) {
              dataToWrite.files.push({
                __typename: 'BucketItem',
                name: `Files/${dataToWrite.folder}/${file.name}`.replace('Files/Files', 'Files'),
                etag: file.name,
                lastModified: file.lastModified,
                prefix: null,
                size: file.size,
                metadata: {
                  __typename: 'BucketMetaData',
                  Fileextension: file.name.split('.').pop(),
                  Filename: file.name,
                  Folder: dataToWrite.folder,
                  Lastaccessed: Date.now(),
                  Label: null,
                  Isstarred: null,
                  Istrashed: null
                }

              });
            }

            cache.writeQuery({
              query: USER_FILES,
              variables: { recursive: true },
              overwrite: false,
              data: {
                exploreDirectoryFiles: {
                  __typename: 'ExploreFilesResponse',
                  paths: [
                    dataToWrite,
                    ...previousPaths.exploreDirectoryFiles.paths.filter(
                      (file) => file.folder.toLowerCase() !== uploadFolder.toLowerCase()
                    )
                  ],
                  message: 'ok',
                  status: 200,
                  errors: null
                }
              }
            });

            return mergeObjects(existing, incoming);
          }
        },
        deleteFiles: {
          merge(_incoming, _existing, { variables, cache }) {
            const deletedFiles = variables.fileNames as string[];

            for (const name of deletedFiles) {
              const normalizedID = cache.identify({ name, __typename: 'BucketItem' });
              cache.evict({ id: normalizedID });
            }
          }
        },
        manageFiles: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          }
        },
        manageFolder: {
          merge(existing, incoming, { mergeObjects, cache, variables }) {
            if (Object.is(variables.action, AppActions.Delete)) {
              for (const folder of variables.foldersToDelete) {
                const normalizedID = cache.identify({ folder, __typename: 'Bucket' });
                cache.evict({ id: normalizedID });
              }
            }
            if (Object.is(variables.action, AppActions.Update)
              || Object.is(variables.action, AppActions.Create)) {
              cache.modify({
                fields: {
                  exploreDirectoryFiles: () => incoming
                }
              });
            }
            return mergeObjects(incoming, existing);
          }
        },
        setupTwoFactorProfile: {
          merge(_existing, incoming, { variables }) {
            const user = reactiveFields.UserSessionData();
            const updatedVars = {
              twoFactorMethod: variables.profile.method
            };
            userSessionVar({ ...user, ...updatedVars });

            return incoming;
          }
        },
        disableTwoFactorAuthentication: {
          merge(_existing, incoming, { cache }) {
            const user = reactiveFields.UserSessionData();

            const updatedVars = {
              twoFactorEnabled: false,
              twoFactorMethod: null
            };
            cache.evict({
              fieldName: 'TwoFactorAuthentication'
            });
            cache.gc();
            userSessionVar({ ...user, ...updatedVars });
            return incoming;
          }
        },
        sendAuthenticationCode: {
          merge(_existing, incoming, _ctx) {
            // TODO: Write to 2FA cache instead of UserSession.
            // (Removed: `console.info(variables)` — leaked the SMS/email
            //  destination the code was sent to, which is user PII.)
            return incoming;
          }
        },
        verifySmsVerificationCode: {
          merge(_existing, incoming) {
            const user = reactiveFields.UserSessionData();
            const updatedVars: Partial<UserSessionData> = {
              twoFactorEnabled: true,
              twoFactorMethod: TwoFactorMethods.Sms
            };

            userSessionVar({ ...user, ...updatedVars });

            return incoming;
          }
        },
        verifyTwilioFactor: {
          merge(_existing, incoming) {
            const user = reactiveFields.UserSessionData();
            const updatedVars: Partial<UserSessionData> = {
              twoFactorEnabled: true
            };
            userSessionVar({ ...user, ...updatedVars });
            return incoming;
          }
        },
        deletePractitionerSignature: {
          merge(_existing, incoming, { cache, variables: { signature: { practitionerId } } }) {
            const cachedSig = cache.readQuery({
              query: GET_PRACTITIONER_SIGNATURE,
              variables: {
                searchBy: {
                  querySet: [
                    {
                      columnName: 'practitioner_id',
                      stringValues: [practitionerId]
                    }
                  ]
                }
              }
            });
            if (cachedSig) {
              removeFromCache('signature_id', cachedSig.getPractitionerSignature.signature_id, cache, 'PractitionerSignatures');
            }
            // The delete records a new audit event; drop the cached timeline so it refetches.
            cache.evict({ fieldName: 'signatureAudits' });
            cache.gc();
            return incoming;
          }
        },
        submitOrderRequest: {
          merge(_, __, { cache, variables }) {
            cache.evict({ fieldName: 'getPaginatedOrders' });
            if (variables?.order?.order?.recurrence) {
              cache.evict({ fieldName: 'getPaginatedRecurringOrders' });
            }
            cache.gc();
          }
        },
        mergeOrders: {
          async merge(_, __, { cache }) {
            await resetCache(cache)
              .catch((error) => {
                if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                  console.warn('[mergeOrders] resetCache failed:', error);
                }
              });
            cache.gc();
          }
        },
        cancelOrder: {
          // Now that cancelled orders stay visible across the app (paranoid:
          // false on the queries), we PATCH the cached order to status =
          // CANCELLED instead of evicting it. Evicting made the row vanish
          // from any list the user was looking at and forced a refresh to
          // see it as "Cancelled".
          merge(_, __, { cache, toReference, variables }) {
            const { orderId, reason } = variables.order;
            const orderRef = toReference({
              __typename: 'Orders',
              order_id: orderId
            });
            const cacheId = cache.identify(orderRef);
            cache.modify({
              id: cacheId,
              fields: {
                status: () => 'CANCELLED',
                status_reason: () => reason ?? 'No reason provided'
              }
            });
          }
        },
        cancelOrderBatch: {
          merge(_, __, { cache, toReference, variables }) {
            const { orders } = variables.batch;
            (orders as Array<{ orderId: string, reason?: string }>)
              .forEach(({ orderId, reason }) => {
                const orderRef = toReference({
                  __typename: 'Orders',
                  order_id: orderId
                });
                const cacheId = cache.identify(orderRef);
                cache.modify({
                  id: cacheId,
                  fields: {
                    status: () => 'CANCELLED',
                    status_reason: () => reason ?? 'No reason provided'
                  }
                });
              });
          }
        },
        // Hard-delete: evict the Orders entity so it drops from every list.
        // Also evict the paginated fields so count/totalPages refetch — entity
        // eviction makes refs dangling but cached metadata stays stale.
        deleteOrder: {
          merge(_, __, { cache, toReference, variables }) {
            const orderRef = toReference({
              __typename: 'Orders',
              order_id: variables.order.orderId
            });
            cache.evict({ id: cache.identify(orderRef) });
            cache.evict({ fieldName: 'getPaginatedOrders' });
            cache.evict({ fieldName: 'getPaginatedRecurringOrders' });
            cache.gc();
          }
        },
        deleteOrderBatch: {
          merge(_, __, { cache, toReference, variables }) {
            (variables.batch.orders as Array<{ orderId: string }>)
              .forEach(({ orderId }) => {
                const orderRef = toReference({
                  __typename: 'Orders',
                  order_id: orderId
                });
                cache.evict({ id: cache.identify(orderRef) });
              });
            cache.evict({ fieldName: 'getPaginatedOrders' });
            cache.evict({ fieldName: 'getPaginatedRecurringOrders' });
            cache.gc();
          }
        },
        deleteTask: {
          merge(_, __, { cache, variables }) {
            // `deleteTask` variables are now `{ payload: { taskId } }`.
            const taskId = variables?.payload?.taskId;
            if (!taskId) return;
            removeFromCache('task_id', taskId, cache, 'Tasks');
          }
        },
        deleteEmployee: {
          merge(_, __, { cache, variables }) {
            const deleteArgs = variables.employee;
            const assignmentIds = deleteArgs?.assignments?.map(
              (a: any) => a.assignmentId
            ) as string[];

            if (isEmpty(assignmentIds)) {
              return;
            }
            assignmentIds.forEach((id) => {
              removeFromCache('assignment_id', id, cache, 'StaffAssignments');
            });
            cache.gc();
          }
        },
        addToCart: {
          merge(_existing, incoming, { toReference, readField, cache }) {
            const cartId = readField<string>('cart_id', incoming);
            const cartRef = toReference({ __typename: 'WebCarts', id: cartId });

            cache.modify({
              id: cache.identify(cartRef),
              fields: {
                items(existing = []) {
                  return [...existing, incoming];
                }
              }
            });
          }
        },

        removeFromCart: {
          merge(_, incoming, { toReference, readField, variables, cache }) {
            const removedItems = map(variables.items, 'itemId');
            const cartId = readField<string>('id', incoming);
            const cartRef = toReference({ __typename: 'WebCarts', id: cartId });

            cache.modify({
              id: cache.identify(cartRef),
              fields: {
                items(existing = []) {
                  return existing.filter(
                    (item: Reference) => !removedItems.includes(readField('id', item))
                  );
                }
              }
            });
          }
        },
        processOrderPayment: {
          merge(_, _incoming, { toReference, variables, cache }) {
            const { orderId } = variables;

            const orderRef = toReference({
              __typename: 'Orders',
              order_id: orderId
            });
            const cacheId = cache.identify(orderRef);
            cache.modify({
              id: cacheId,
              fields: {
                balance() {
                  return '$0.00';
                }
              }
            });
          }
        },
        updateEventSeries: {
          merge(_, incoming, { cache, variables, readField, toReference }) {
            const seriesVariables = variables.series as UpdateEventSeriesMutationVariables['series'];

            const instanceSeriesId = readField('series_id', incoming);
            if (
              seriesVariables.seriesId
              !== instanceSeriesId
            ) {
              const seriesToRemoveRef = toReference({
                __typename: 'EventSeries',
                series_id: seriesVariables.seriesId
              });
              cache.evict({ id: cache.identify(seriesToRemoveRef) });
            }
          }
        },
        mergeRecords: {
          async merge(_, __, { cache }) {
            await resetCache(cache)
              .catch((error) => {
                if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                  console.warn('[mergeRecords] resetCache failed:', error);
                }
              });
          }
        }
      }
    },
    Subscription: {
      fields: {}
    },
    // ManageOrderLineItems: {
    //   fields: {
    //     diagnosis_codes: {
    //       merge(existing, incoming) {
    //         console.log('Existing', existing);
    //         console.log('Incomnig', incoming);
    //         return incoming;
    //       }
    //     }
    //   }
    // },
    // OrderedServicesColumn: {
    //   queryType: true,
    //   fields: {
    //     getAllServiceRequests(existing, {cache, variables }) {
    //       console.log('Get all services requests field');
    //       console.log('Existing', existing);
    //       console.log('Variables', variables);
    //       cache.evict({fieldName: ''})
    //
    //     }
    //   },
    //   merge: (existing, incoming, { variables }) => {
    //     console.log('Existing', existing);
    //     console.log('Incoming', incoming);
    //     console.log('Variables', variables);
    //   }
    // },
    Addresses: {
      keyFields: ['address_id']
    },
    Agents: {
      keyFields: ['agent_id']
    },
    AgentClassifications: {
      keyFields: ['classification_number']
    },
    AgentClassResistance: {
      keyFields: ['class_resistance_id']
    },
    Annotations: {
      keyFields: ['annotation_id']
    },
    Appointments: {
      keyFields: ['appointment_id']
    },
    Coverages: {
      keyFields: ['coverage_id']
    },
    CoverageClasses: {
      keyFields: ['coverage_class_id']
    },
    AppointmentServiceTypes: {
      keyFields: ['type_id']
    },
    Attachments: {
      keyFields: ['attachment_id']
    },
    Attributes: {
      keyFields: ['attribute_id']
    },
    Bucket: {
      keyFields: ['folder']
    },
    BucketItem: {
      keyFields: ['name']
    },
    BucketMetaData: {
      keyFields: ['Filename']
    },
    CarePlans: {
      keyFields: ['care_plan_id']
    },
    CareTeams: {
      keyFields: ['care_team_id']
    },
    CareTeamMembers: {
      keyFields: ['member_id']
    },
    ClinicalNotes: {
      keyFields: ['note_id']
    },
    CodeableConcepts: {
      keyFields: ['concept_id']
    },
    CodeableReferences: {
      keyFields: ['reference_id']
    },
    CollectionDays: {
      merge(existing = [], incoming) {
        return deepMerge(existing, incoming);
      },
      keyFields: ['collection_day_id', 'collection_day']
    },
    CollectionDayTimeSlots: {
      keyFields: ['time_slot_id']
    },
    CollectionDayTimeBlocks: {
      keyFields: ['time_block_id']
    },
    Conditions: {
      keyFields: ['condition_id']
    },
    ContactDetails: {
      keyFields: ['contact_detail_id']
    },
    ContactPoints: {
      keyFields: ['contact_point_id']
    },
    CriteriaFormResults: {
      keyFields: ['result_id']
    },
    CriteriaFormResultFlags: {
      keyFields: ['flag_id']
    },
    CynergyUsers: {
      keyFields: ['cynergy_id']
    },
    Demographics: {
      keyFields: ['cynergy_id']
    },
    DetectedIssues: {
      keyFields: ['issue_id']
    },
    DiagnosticReports: {
      keyFields: ['report_id']
    },
    DiagnosticServices: {
      keyFields: ['service_id']
    },
    DuplicateRecordEntries: {
      keyFields: ['entry_id']
    },
    DuplicateRecordSets: {
      keyFields: ['set_id']
    },
    ExploreFilesResponse: {
      merge: (existing, incoming, { mergeObjects }) => mergeObjects(existing, incoming)
    },
    Events: {
      keyFields: ['event_id']
    },
    EventSeries: {
      keyFields: ['series_id']
    },
    // FeedItemObject: {
    //
    // },
    Genotypes: {
      keyFields: ['genotype_id']
    },
    ICD10: {
      keyFields: ['icd10_code']
    },
    Identifiers: {
      keyFields: ['identifier_id']
    },
    Locations: {
      keyFields: ['location_id']
    },
    LocationPatients: {
      keyFields: ['patient_id', 'location_id']
    },
    Medications: {
      keyFields: ['medication_id']
    },
    MedicationUsages: {
      keyFields: ['usage_id']
    },
    MonthlyRecurrencePatterns: {
      keyFields: ['pattern_id']
    },
    Metadata: {
      keyFields: ['meta_id']
    },
    HealthcareServices: {
      keyFields: ['service_id']
    },
    HealthcareServiceLocations: {
      keyFields: ['service_location_id']
    },
    NewCartTTLPayload: {
      keyFields: ['topic']
    },
    Notifications: {
      keyFields: ['notification_id']
    },
    Observations: {
      keyFields: ['observation_id']
    },
    ObservationDefinitions: {
      keyFields: ['definition_id']
    },
    ObservationFlags: {
      keyFields: ['flag_id']
    },
    ObservationResultValues: {
      keyFields: ['value_id']
    },
    ObservationReferenceRanges: {
      keyFields: ['reference_range_id']
    },
    ObservationTriggeredBy: {
      keyFields: ['trigger_id']
    },
    Orders: {
      keyFields: ['order_id'],
      fields: {
        diagnosis_codes: {
          merge: false
        }
      }
      // fields: {
      //   members(_, { cache, readField }) {
      //     const cachedData = cache.extract();
      //     const seriesId = readField<string>('series_id');
      //     const orderId = readField<string>('order_id');
      //
      //     return Object.values(cachedData)
      //       .filter((entry: any) => {
      //         if (entry.__typename !== 'Orders' || orderId === entry.order_id) {
      //           return false;
      //         }
      //         return entry?.series_id && entry?.series_id === seriesId;
      //       });
      //   }
      // }
    },
    OrderAgents: {
      keyFields: ['agent_id']
    },
    OrderLineItems: {
      keyFields: ['line_item_id']
    },
    OrderSignatures: {
      keyFields: ['order_id']
    },
    OrganismCounts: {
      keyFields: ['organism']
    },
    Organizations: {
      keyFields: ['organization_id']
    },
    Patients: {
      keyFields: ['patient_id']
    },
    Periods: {
      keyFields: ['period_id']
    },
    Practitioners: {
      keyFields: ['practitioner_id']
      // fields: {
      //   role: {
      //     read(_, { variables, readField }){
      //       const session = reactiveFields.UserSessionData();
      //       const affiliations: any[] = readField({
      //         // eslint-disable-next-line no-underscore-dangle
      //         fieldName: 'location_affiliations',
      //         typename: 'Practitioners'
      //       }) as any[];
      //       const getLocationRole = (locationId: string) => {
      //         const foundRole = affiliations.find(
      //           (affiliation) => affiliation.location_id === locationId
      //         );
      //         if (foundRole){
      //           return foundRole.role;
      //         }
      //         return 'Default';
      //       };
      //
      //       if (affiliations && !isEmpty(affiliations)){
      //         const locationArg = variables?.searchBy?.querySet?.find((q) => q.columnName.includes('location_id'));
      //
      //         if (locationArg) {
      //           return getLocationRole(locationArg.stringValues.at(-1));
      //         }
      //
      //         return getLocationRole(session.locationId);
      //       }
      //       return 'Default';
      //     }
      //   }
      // }
    },
    PractitionerLocationAffiliations: {
      keyFields: ['affiliation_id']
    },
    PractitionerQualifications: {
      keyFields: ['qualification_id']
    },
    PractitionerSignatures: {
      keyFields: ['signature_id']
    },
    PriceBooks: {
      keyFields: ['book_id']
    },
    PriceBookEntries: {
      keyFields: ['entry_id']
    },
    Products: {
      keyFields: ['code'],
      fields: {
        isWishListed() {
          return false;
        },
        isInCart(_, { readField, cache }) {
          const code = readField<string>('code');
          const cartQuery = cache.readQuery({
            query: USER_CART
          });

          const cartCache = cartQuery?.cart;
          if (cartCache && !isEmpty(cartCache?.items)) {
            const itemProductCodes = map(cartCache.items, 'productCode')
              .filter(Boolean);
            return itemProductCodes.includes(code);
          }
          return false;
        }

      }
    },
    ProductCatalogs: {
      keyFields: ['catalog_id']
    },
    ProductCatalogItems: {
      keyFields: ['item_id']
    },
    ProductCategories: {
      keyFields: ['category_id']
    },
    Quantity: {
      keyFields: ['quantity_id']
    },
    Questionnaires: {
      keyFields: ['questionnaire_number']
    },
    QuestionnaireResponses: {
      keyFields: ['response_id']
    },
    QuestionnaireItems: {
      keyFields: ['item_id']
    },
    RecurrencePatterns: {
      keyFields: ['pattern_id']
    },
    Ratio: {
      keyFields: ['ratio_id']
    },
    ServiceRequests: {
      keyFields: ['request_id'],
      merge: true
    },
    Signatures: {
      keyFields: ['signature_id']
    },
    SpecimenDefinitions: {
      keyFields: ['definition_id']
    },
    SpecimenDefinitionCollections: {
      keyFields: ['collection_id']
    },
    Staffs: {
      keyFields: ['staff_id']
    },
    StaffAssignments: {
      keyFields: ['assignment_id']
    },
    Tags: {
      keyFields: ['tag_id']
    },
    Tasks: {
      keyFields: ['task_id']
    },
    TaskSteps: {
      keyFields: ['step_id']
    },
    Taxonomies: {
      keyFields: ['taxonomy_id']
    },
    TwoFactorAuthentication: {
      keyFields: ['two_factor_id']
    },
    TwoFactorPush: {
      keyFields: ['two_factor_id']
    },
    TwoFactorTotp: {
      keyFields: ['two_factor_id']
    },
    UserProductBundles: {
      keyFields: ['bundle_id']
    },
    UserProductBundleItems: {
      keyFields: ['bundle_item_id']
    },
    UserSession: {
      fields: {
        UserCart: {
          read() {
            return reactiveFields.UserCartData();
          }
        }
      }
    },
    Users: {
      keyFields: ['user_id']
    },
    WebCartSummaries: {
      keyFields: ['cart_id']
    },
    WeeklyRecurrencePatterns: {
      keyFields: ['pattern_id']
    }
  }
});
